import {
  EuiErrorBoundary,
  EuiFormRow,
  EuiText,
  EuiForm,
  EuiFieldNumber,
  EuiFieldText,
  EuiDescribedFormGroup,
  EuiRadioGroup,
} from '@elastic/eui';
import { Configuration } from 'app/generated/graphql';
import { Control, Controller } from 'react-hook-form';

import * as GPS_CONSTANTS from './gps-config-defaults';
import { ConfigPanel } from '../../panel/config-panel';
import { parseNumberOnChange } from '../snt-config-utils';

interface SntGpsConfigViewProps {
  /** Used for handling the state of the panel. Collapsed or expanded. */
  isExpanded: boolean;
  /** react-hook-form controller for monitoring form state */
  control: Control<Configuration>;
}

// Groups version has NEITHER option selected, so we have to use radio
// buttons instead of an on/off switch
const DISABLED_ID = `allow-2d-disabled`;
const ENABLED_ID = `allow-2d-enabled`;
const RADIO_BUTTONS = [
  { id: DISABLED_ID, label: 'Disable' },
  { id: ENABLED_ID, label: 'Enable' },
];

/** GPS configuration fields for a Slap and Track device */
export const SntGpsConfig = (props: SntGpsConfigViewProps) => {
  /** Methods */
  const handleAllow2DEnabledToggle = (radioValue: string, setValue: (value: boolean) => void) => {
    radioValue === ENABLED_ID ? setValue(true) : setValue(false);
  };

  // This method is used to determine the selected ID for the allow 2D GPS radio button
  // One of the options will always be selected, reflecting the user's latest selection
  const conjureSelectedId = (currentValue: boolean | string | null | undefined) => {
    if (currentValue === true || currentValue === 'true') {
      return ENABLED_ID;
    }
    if (currentValue === false || currentValue === 'false') {
      return DISABLED_ID;
    }
    // undefined, null, '' or other: use default so one option is always selected
    return GPS_CONSTANTS.ALLOW_2D_DEFAULT ? ENABLED_ID : DISABLED_ID;
  };

  return (
    <EuiErrorBoundary>
      <EuiForm component="form">
        <ConfigPanel
          expandAll={props.isExpanded}
          title="GPS"
          icon="globe"
          description="How the tracker determines its location"
          isLoading={false}
        >
          <EuiDescribedFormGroup
            title={<h3>AGPS Server</h3>}
            description={
              <EuiText size="s">
                <p>
                  The AGPS server and port to use for Assisted GPS. If this is set to an empty
                  string, AGPS will not be used. This must be a SUPL AGPS server.
                </p>
                <p>{`Default: ${GPS_CONSTANTS.AGPS_SERVER_DEFAULT}`}</p>
              </EuiText>
            }
          >
            <Controller
              name="gps.agpsServer"
              control={props.control}
              render={({ field: { onChange, onBlur, value } }) => (
                <EuiFormRow
                  label="AGPS Server"
                  helpText={
                    <>
                      Leave empty to disable AGPS. Has to be a SUPL AGPS server.
                      <br />
                      Format: server:port (e.g. supl.google.com:7276)
                    </>
                  }
                >
                  <EuiFieldText
                    onBlur={onBlur}
                    value={value || ''}
                    onChange={event => onChange?.(event.target.value)}
                    placeholder="supl.google.com:7276"
                  />
                </EuiFormRow>
              )}
            />
          </EuiDescribedFormGroup>

          <EuiDescribedFormGroup
            title={<h3>Allow 2D GPS</h3>}
            description={
              <EuiText size="s">
                <p>
                  Enable or disable if the device will allow 2 dimensional GPS fix. 2D fixes will be
                  of lower accuracy, with unreliable altitude information. If using the &quot;Stolen
                  Asset&quot; feature, enabling 2D fixes can cause false triggers due to the lower
                  accuracy of the 2D GPS fix.
                </p>
                <p>{`Default: ${GPS_CONSTANTS.ALLOW_2D_DEFAULT ? 'Enabled' : 'Disabled'}`}</p>
              </EuiText>
            }
          >
            <Controller
              name="gps.allow2d"
              control={props.control}
              render={({ field: { onChange, onBlur, value } }) => (
                <EuiFormRow label="Allow 2D GPS">
                  <EuiRadioGroup
                    options={RADIO_BUTTONS}
                    idSelected={conjureSelectedId(value!)}
                    onChange={radioValue => handleAllow2DEnabledToggle(radioValue, onChange)}
                    onBlur={onBlur}
                    name="allow-2d-toggle"
                  />
                </EuiFormRow>
              )}
            />
          </EuiDescribedFormGroup>

          <EuiDescribedFormGroup
            title={<h3>SAT3 Timeout</h3>}
            description={
              <EuiText size="s">
                <p>
                  The interval in seconds if during a GPS fix attempt, at least 3 usable satellites
                  are not in view. Lower timeouts can improve battery life. Higher timeouts can
                  improve chances of obtaining a GPS fix in locations with a limited sky view.
                </p>
                <p>{`Default: ${GPS_CONSTANTS.SAT3_TIMEOUT_DEFAULT} seconds`}</p>
              </EuiText>
            }
          >
            <Controller
              name="gps.sat3Timeout"
              control={props.control}
              rules={{
                min: {
                  value: GPS_CONSTANTS.SAT3_TIMEOUT_MIN,
                  message: `Must be at least ${GPS_CONSTANTS.SAT3_TIMEOUT_MIN}`,
                },
                max: {
                  value: GPS_CONSTANTS.SAT3_TIMEOUT_MAX,
                  message: `Must be at most ${GPS_CONSTANTS.SAT3_TIMEOUT_MAX}`,
                },
              }}
              render={({ fieldState: { invalid, error }, field: { onChange, onBlur, value } }) => (
                <EuiFormRow
                  label="SAT3 Timeout"
                  isInvalid={invalid}
                  error={error?.message}
                  helpText={`Valid range: ${GPS_CONSTANTS.SAT3_TIMEOUT_MIN} - ${GPS_CONSTANTS.SAT3_TIMEOUT_MAX} seconds`}
                >
                  <EuiFieldNumber
                    onBlur={onBlur}
                    min={GPS_CONSTANTS.SAT3_TIMEOUT_MIN}
                    max={GPS_CONSTANTS.SAT3_TIMEOUT_MAX}
                    value={value!}
                    onChange={event => onChange?.(parseNumberOnChange(event))}
                    placeholder={GPS_CONSTANTS.SAT3_TIMEOUT_DEFAULT.toString()}
                    append={['seconds']}
                  />
                </EuiFormRow>
              )}
            />
          </EuiDescribedFormGroup>

          <EuiDescribedFormGroup
            title={<h3>AGPS Comm Timeout</h3>}
            description={
              <EuiText size="s">
                <p>
                  The interval in seconds to attempt to connect to the network to obtain AGPS data.
                  This is only the timeout for attempting to connect.
                </p>
                <p>{`Default: ${GPS_CONSTANTS.AGPS_COMM_TIMEOUT_DEFAULT} seconds`}</p>
              </EuiText>
            }
          >
            <Controller
              name="gps.agpsCommTimeout"
              control={props.control}
              rules={{
                min: {
                  value: GPS_CONSTANTS.AGPS_COMM_TIMEOUT_MIN,
                  message: `Must be at least ${GPS_CONSTANTS.AGPS_COMM_TIMEOUT_MIN}`,
                },
                max: {
                  value: GPS_CONSTANTS.AGPS_COMM_TIMEOUT_MAX,
                  message: `Must be at most ${GPS_CONSTANTS.AGPS_COMM_TIMEOUT_MAX}`,
                },
              }}
              render={({ fieldState: { invalid, error }, field: { onChange, onBlur, value } }) => (
                <EuiFormRow
                  label="AGPS CommTimeout"
                  isInvalid={invalid}
                  error={error?.message}
                  helpText={`Valid range: ${GPS_CONSTANTS.AGPS_COMM_TIMEOUT_MIN} - ${GPS_CONSTANTS.AGPS_COMM_TIMEOUT_MAX} seconds`}
                >
                  <EuiFieldNumber
                    onBlur={onBlur}
                    min={GPS_CONSTANTS.AGPS_COMM_TIMEOUT_MIN}
                    max={GPS_CONSTANTS.AGPS_COMM_TIMEOUT_MAX}
                    value={value!}
                    onChange={event => onChange?.(parseNumberOnChange(event))}
                    placeholder={GPS_CONSTANTS.AGPS_COMM_TIMEOUT_DEFAULT.toString()}
                    append={['seconds']}
                  />
                </EuiFormRow>
              )}
            />
          </EuiDescribedFormGroup>

          <EuiDescribedFormGroup
            title={<h3>AGPS Data Retries</h3>}
            description={
              <EuiText size="s">
                <p>
                  Once a network connection has been established for obtaining AGPS data, the number
                  of times to try obtaining AGPS data if the first attempt fails. Retries will occur
                  if the network connection remains connected.
                </p>
                <p>{`Default: ${GPS_CONSTANTS.AGPS_DATA_RETRIES_DEFAULT} times`}</p>
              </EuiText>
            }
          >
            <Controller
              name="gps.agpsDataRetries"
              control={props.control}
              rules={{
                min: {
                  value: GPS_CONSTANTS.AGPS_DATA_RETRIES_MIN,
                  message: `Must be at least ${GPS_CONSTANTS.AGPS_DATA_RETRIES_MIN}`,
                },
                max: {
                  value: GPS_CONSTANTS.AGPS_DATA_RETRIES_MAX,
                  message: `Must be at most ${GPS_CONSTANTS.AGPS_DATA_RETRIES_MAX}`,
                },
              }}
              render={({ fieldState: { invalid, error }, field: { onChange, onBlur, value } }) => (
                <EuiFormRow
                  label="AGPS Data Retries"
                  isInvalid={invalid}
                  error={error?.message}
                  helpText={`Valid range: ${GPS_CONSTANTS.AGPS_DATA_RETRIES_MIN} - ${GPS_CONSTANTS.AGPS_DATA_RETRIES_MAX} times`}
                >
                  <EuiFieldNumber
                    onBlur={onBlur}
                    min={GPS_CONSTANTS.AGPS_DATA_RETRIES_MIN}
                    max={GPS_CONSTANTS.AGPS_DATA_RETRIES_MAX}
                    value={value!}
                    onChange={event => onChange?.(parseNumberOnChange(event))}
                    placeholder={GPS_CONSTANTS.AGPS_DATA_RETRIES_DEFAULT.toString()}
                    append={['times']}
                  />
                </EuiFormRow>
              )}
            />
          </EuiDescribedFormGroup>
        </ConfigPanel>
      </EuiForm>
    </EuiErrorBoundary>
  );
};
